import React from 'react'
import { Box, Paper, Skeleton, Tooltip, Typography, alpha, useTheme } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import type {
  KnownQueueBatchState,
  QueueBatch,
  QueueBatchState,
  QueueLane,
  QueueTotals,
} from '../../types/autocoderQueue'
import {
  DEFAULT_MAX_TILES,
  buildLaneView,
  formatDuration,
  formatTimestamp,
  stateGlyph,
  stateLabel,
  windowNote,
  type LaneView,
} from './laneQueueModel'

/**
 * LaneQueueView — lanes as rows, batches as discrete tiles.
 *
 * The point of this view is to make a batch feel like a *thing*: you can see
 * work pile up on the right, get pulled into flight in the middle, and land as
 * done on the left. The in-flight cap is stated per lane because it is the
 * reason only N run at once.
 *
 * Purely presentational — the caller supplies `lanes`; nothing is fetched here.
 */

export interface LaneQueueViewProps {
  lanes: QueueLane[]
  loading?: boolean
  /** Tiles rendered per lane before older completed batches fold up. */
  maxTilesPerLane?: number
  /**
   * The pipeline-wide UNBOUNDED census and the bound applied to `batches[]`.
   * Passed so the panel can disclose that the tiles are a window rather than
   * implying it holds every batch the pipeline ever had.
   */
  totals?: QueueTotals
  batchLimit?: number
}

interface StateStyle {
  fg: string
  bg: string
  border: string
}

const stateStyles = (theme: Theme, state: QueueBatchState): StateStyle => {
  const isDark = theme.palette.mode === 'dark'
  switch (state) {
    case 'completed':
      return {
        fg: isDark ? theme.palette.success.light : theme.palette.success.dark,
        bg: alpha(theme.palette.success.main, isDark ? 0.16 : 0.1),
        border: alpha(theme.palette.success.main, isDark ? 0.45 : 0.35),
      }
    case 'running':
      return {
        fg: isDark ? theme.palette.info.light : theme.palette.info.dark,
        bg: alpha(theme.palette.info.main, isDark ? 0.22 : 0.14),
        border: alpha(theme.palette.info.main, isDark ? 0.75 : 0.6),
      }
    case 'failed':
      return {
        fg: isDark ? theme.palette.error.light : theme.palette.error.dark,
        bg: alpha(theme.palette.error.main, isDark ? 0.18 : 0.12),
        border: alpha(theme.palette.error.main, isDark ? 0.7 : 0.55),
      }
    case 'pending':
    default:
      // Also the fallback for a state this build has never heard of: neutral,
      // and the tile still carries the raw state name in its label.
      return {
        fg: theme.palette.text.secondary,
        bg: alpha(theme.palette.text.primary, isDark ? 0.06 : 0.04),
        border: alpha(theme.palette.text.primary, isDark ? 0.22 : 0.16),
      }
  }
}

const batchTooltip = (batch: QueueBatch): React.ReactNode => {
  const started = formatTimestamp(batch.startedAt)
  const completed = formatTimestamp(batch.completedAt)
  const duration = formatDuration(batch.startedAt, batch.completedAt)
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
        {stateLabel(batch.state)} · {batch.records.toLocaleString()} records
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
        Batch {batch.id}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
        Queued {formatTimestamp(batch.createdAt) ?? '—'}
        {started ? ` · started ${started}` : ''}
        {completed ? ` · finished ${completed}` : ''}
        {duration ? ` · took ${duration}` : ''}
      </Typography>
      {batch.error ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
          Error: {batch.error}
        </Typography>
      ) : null}
    </Box>
  )
}

const batchAriaLabel = (batch: QueueBatch): string => {
  const parts = [
    `${stateLabel(batch.state)} batch`,
    `${batch.records} records`,
    `queued ${formatTimestamp(batch.createdAt) ?? 'unknown time'}`,
  ]
  const duration = formatDuration(batch.startedAt, batch.completedAt)
  if (duration) parts.push(`took ${duration}`)
  if (batch.error) parts.push(`error: ${batch.error}`)
  return parts.join(', ')
}

const BatchTile: React.FC<{ batch: QueueBatch }> = ({ batch }) => {
  const theme = useTheme()
  const s = stateStyles(theme, batch.state)
  const isRunning = batch.state === 'running'

  return (
    <Tooltip title={batchTooltip(batch)} arrow enterDelay={150}>
      <Box
        component="li"
        tabIndex={0}
        aria-label={batchAriaLabel(batch)}
        sx={{
          listStyle: 'none',
          flex: '0 0 auto',
          width: 52,
          minHeight: 56,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.25,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: s.border,
          backgroundColor: s.bg,
          color: s.fg,
          cursor: 'default',
          transition: theme.transitions.create(['transform', 'box-shadow', 'border-color'], {
            duration: theme.transitions.duration.shorter,
          }),
          outlineOffset: 2,
          '&:hover, &:focus-visible': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[3],
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.info.main}`,
          },
          ...(batch.state === 'failed' && {
            // Shape, not colour alone: failed tiles carry a heavier dashed edge.
            borderStyle: 'dashed',
            borderWidth: 2,
          }),
          ...(isRunning && {
            borderWidth: 2,
            '@media (prefers-reduced-motion: no-preference)': {
              animation: 'laneQueuePulse 1.8s ease-in-out infinite',
            },
            '@keyframes laneQueuePulse': {
              '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.info.main, 0.35)}` },
              '50%': { boxShadow: `0 0 0 5px ${alpha(theme.palette.info.main, 0)}` },
            },
          }),
        }}
      >
        <Box aria-hidden sx={{ fontSize: 14, lineHeight: 1, fontWeight: 700 }}>
          {stateGlyph(batch.state)}
        </Box>
        <Typography
          aria-hidden
          variant="caption"
          sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
        >
          {batch.records}
        </Typography>
      </Box>
    </Tooltip>
  )
}

const CollapsedDoneTile: React.FC<{ count: number }> = ({ count }) => {
  const theme = useTheme()
  const s = stateStyles(theme, 'completed')
  return (
    <Tooltip
      title={`${count.toLocaleString()} earlier completed batches, folded away to keep the live end readable`}
      arrow
    >
      <Box
        component="li"
        tabIndex={0}
        aria-label={`${count} earlier completed batches, not shown individually`}
        sx={{
          listStyle: 'none',
          flex: '0 0 auto',
          minWidth: 52,
          px: 1,
          minHeight: 56,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 1.5,
          border: '1px dashed',
          borderColor: s.border,
          backgroundColor: alpha(theme.palette.success.main, 0.05),
          color: s.fg,
          cursor: 'default',
          outlineOffset: 2,
          '&:focus-visible': { outline: `2px solid ${theme.palette.info.main}` },
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          +{count.toLocaleString()}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.8, lineHeight: 1.2 }}>
          done
        </Typography>
      </Box>
    </Tooltip>
  )
}

const LaneRow: React.FC<{ view: LaneView; divided: boolean }> = ({ view, divided }) => {
  const theme = useTheme()
  const capColor = view.atCap ? theme.palette.info.main : theme.palette.text.secondary

  return (
    <Box
      component="section"
      aria-label={`${view.displayName} lane, ${view.capLabel}`}
      sx={{
        py: 1.5,
        ...(divided && { borderTop: `1px solid ${theme.palette.divider}` }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          columnGap: 1.5,
          rowGap: 0.5,
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
          {view.displayName}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace' }}>
          {view.lane}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 999,
            border: '1px solid',
            borderColor: alpha(capColor, 0.4),
            backgroundColor: alpha(capColor, view.atCap ? 0.12 : 0.06),
            color: capColor,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {view.capLabel}
          </Typography>
        </Box>
      </Box>

      {view.isEmpty ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
          No batches on this lane.
        </Typography>
      ) : (
        <Box
          component="ol"
          role="list"
          aria-label={`Batches on ${view.displayName}, oldest done first, waiting last`}
          sx={{
            m: 0,
            p: 0.5,
            display: 'flex',
            alignItems: 'stretch',
            gap: 0.75,
            overflowX: 'auto',
            scrollbarWidth: 'thin',
          }}
        >
          {view.collapsedDoneCount > 0 ? <CollapsedDoneTile count={view.collapsedDoneCount} /> : null}
          {view.tiles.map((batch) => (
            <BatchTile key={batch.id} batch={batch} />
          ))}
        </Box>
      )}

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {view.completedCount.toLocaleString()} done
        {view.failedCount > 0 ? ` · ${view.failedCount.toLocaleString()} failed` : ''}
        {' · '}
        {view.runningCount.toLocaleString()} running · {view.pendingCount.toLocaleString()} waiting
        {view.pendingRecords > 0 ? ` (${view.pendingRecords.toLocaleString()} records)` : ''}
        {view.otherCount > 0 ? ` · ${view.otherCount.toLocaleString()} in another state` : ''}
      </Typography>
    </Box>
  )
}

const Legend: React.FC = () => {
  const theme = useTheme()
  const items: KnownQueueBatchState[] = ['completed', 'running', 'pending', 'failed']
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.5 }}>
      {items.map((state) => {
        const s = stateStyles(theme, state)
        return (
          <Box key={state} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              aria-hidden
              sx={{
                width: 18,
                height: 18,
                borderRadius: 0.75,
                border: state === 'failed' ? '2px dashed' : '1px solid',
                borderColor: s.border,
                backgroundColor: s.bg,
                color: s.fg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {stateGlyph(state)}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {stateLabel(state)}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}

export const LaneQueueView: React.FC<LaneQueueViewProps> = ({
  lanes,
  loading = false,
  maxTilesPerLane = DEFAULT_MAX_TILES,
  totals,
  batchLimit,
}) => {
  const views = React.useMemo(
    () => (lanes ?? []).map((lane) => buildLaneView(lane, { maxTiles: maxTilesPerLane })),
    [lanes, maxTilesPerLane],
  )

  // How many batches are actually on screen, against how many exist. Every
  // COUNT in this panel comes from the unbounded totals; the tiles do not, and
  // the reader is told which is which.
  const shown = React.useMemo(
    () => (lanes ?? []).reduce((n, l) => n + (l.batches?.length ?? 0), 0),
    [lanes],
  )
  const disclosure = windowNote(shown, totals, batchLimit)

  if (loading && views.length === 0) {
    return (
      <Paper elevation={0} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Skeleton variant="text" width={180} height={28} />
        <Skeleton variant="rounded" height={72} sx={{ mt: 1 }} />
        <Skeleton variant="rounded" height={72} sx={{ mt: 1 }} />
      </Paper>
    )
  }

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{ p: 2, borderRadius: 2, opacity: loading ? 0.75 : 1 }}
      aria-busy={loading || undefined}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Coding queue
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Each tile is one batch of records. Work moves left to right: done, in flight, waiting.
      </Typography>
      {disclosure && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled' }}>
          {disclosure} The counts under each lane are the full totals, not the tiles.
        </Typography>
      )}

      {views.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 3 }}>
          No lanes are carrying work for this pipeline right now.
        </Typography>
      ) : (
        <Box sx={{ mt: 1 }}>
          {views.map((view, i) => (
            <LaneRow key={view.lane} view={view} divided={i > 0} />
          ))}
        </Box>
      )}

      <Legend />
    </Paper>
  )
}

export default LaneQueueView
